import { Box, Button } from '@material-ui/core'
import { GridFooter, GridFooterContainer } from '@mui/x-data-grid'
import React from 'react'
import { Download } from 'react-feather'
import csvDownload from 'json-to-csv-export'
import { useTranslation } from 'react-i18next'
import { useNotifications } from '../../notifications/use-notifications'
import { useProjectsLazyQuery } from '../../generated/graphql'
import { createProjectListFilter } from './util/project-util'
import { IconSize } from '../common/Icon'
import {
  ProjectListKeys,
  useProjectListData,
} from './util/use-project-list-data'
import { ProjectSearchFormValues } from './ProjectSearchForm'

const MAX_PROJECT_COUNT = 1000

type Props = ProjectSearchFormValues & {
  onExport: () => void
}

export function ProjectListGridFooter({
  keyword,
  createdAfter,
  onExport,
}: Props): React.ReactElement {
  const { enqueueInfo, enqueueWarning, enqueueError, closeSnackbar } =
    useNotifications()
  const { t } = useTranslation()
  const { keyTranslations, mapProjectToListData } = useProjectListData()
  const [getProjects] = useProjectsLazyQuery()

  const handleExport = async () => {
    const info = enqueueInfo(t('projects.fetchingExportData'))
    try {
      const response = await getProjects({
        variables: createProjectListFilter(
          MAX_PROJECT_COUNT,
          0,
          keyword,
          createdAfter,
        ),
      })
      onExport()
      if (!response.data) {
        throw new Error('Failed to get projects data.')
      }
      if (response.data.projects.totalCount > MAX_PROJECT_COUNT) {
        enqueueWarning(
          t('projects.exportLimitExceeded', { max: MAX_PROJECT_COUNT }),
        )
      }

      const headers = Object.values(ProjectListKeys)
        .filter((key) => key !== ProjectListKeys.Id)
        .map((key) => keyTranslations[key])
      const data = response.data.projects.items
        .map(mapProjectToListData)
        .map(({ id, ...rest }) => ({ ...rest }))
      const json = {
        headers,
        data,
        filename: 'Projects',
      }
      csvDownload(json)
      closeSnackbar(info)
    } catch (error) {
      enqueueError(`Error while exporting projects to CSV: ${String(error)}`)
    }
  }
  return (
    <GridFooterContainer>
      <Box display="flex" width="100%" justifyContent="space-between">
        <Button
          startIcon={<Download size={IconSize.Small} />}
          onClick={handleExport}
          data-cy="csv-export"
        >
          {t('dataGrid.exportCSV')}
        </Button>
        <GridFooter />
      </Box>
    </GridFooterContainer>
  )
}
